import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  Decision,
  DecisionStatus,
  DecisionImpact,
  DecisionAnalytics,
} from '@/models/team-decision.model';
import {
  auditCreate,
  auditUpdate,
  auditDelete,
} from '@/utils/audit-integration';
import { AuditEntityType, AuditLogAction } from '@/models/audit-log.model';
import { AuditLogService } from './audit-log.service';

const DECISIONS_COLLECTION = 'teamDecisions';

export class TeamDecisionService {
  /**
   * Create a new decision (typically from AI extraction)
   */
  static async createDecision(
    decisionData: Partial<Decision>,
    userId: string = 'ai-system',
    userName: string = 'AI System'
  ): Promise<string> {
    const now = Timestamp.now();

    const decision: Partial<Decision> = {
      ...decisionData,
      status: decisionData.status || DecisionStatus.draft,
      containsPHI: decisionData.containsPHI || false,
      phiDataTypes: decisionData.phiDataTypes || [],
      tags: decisionData.tags || [],
      relatedDecisionIds: decisionData.relatedDecisionIds || [],
      affectedParties: decisionData.affectedParties || [],
      createdBy: userId,
      createdByName: userName,
      createdAt: now,
      updatedAt: now,
    };

    const docRef = await addDoc(collection(db, DECISIONS_COLLECTION), decision);

    // Audit log
    await auditCreate(
      AuditEntityType.teamDecision,
      docRef.id,
      decisionData.title || 'Untitled Decision'
    );

    return docRef.id;
  }

  /**
   * Get decision by ID
   */
  static async getDecisionById(id: string): Promise<Decision | null> {
    const docRef = doc(db, DECISIONS_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return this.docToDecision(docSnap.id, docSnap.data());
  }

  /**
   * Get all decisions for a meeting
   */
  static async getDecisionsByMeeting(meetingId: string): Promise<Decision[]> {
    const q = query(
      collection(db, DECISIONS_COLLECTION),
      where('meetingId', '==', meetingId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToDecision(doc.id, doc.data()));
  }

  /**
   * Get all decisions
   */
  static async getAllDecisions(): Promise<Decision[]> {
    const q = query(
      collection(db, DECISIONS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToDecision(doc.id, doc.data()));
  }

  /**
   * Get decisions by status
   */
  static async getDecisionsByStatus(status: DecisionStatus): Promise<Decision[]> {
    const q = query(
      collection(db, DECISIONS_COLLECTION),
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToDecision(doc.id, doc.data()));
  }

  /**
   * Get decisions by impact level
   */
  static async getDecisionsByImpact(impact: DecisionImpact): Promise<Decision[]> {
    const q = query(
      collection(db, DECISIONS_COLLECTION),
      where('impact', '==', impact),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToDecision(doc.id, doc.data()));
  }

  /**
   * Search decisions by title or description
   */
  static async searchDecisions(searchTerm: string): Promise<Decision[]> {
    const allDecisions = await this.getAllDecisions();
    const lowerSearch = searchTerm.toLowerCase();

    return allDecisions.filter(decision =>
      decision.title.toLowerCase().includes(lowerSearch) ||
      decision.description.toLowerCase().includes(lowerSearch) ||
      decision.category?.toLowerCase().includes(lowerSearch)
    );
  }

  /**
   * Update decision
   */
  static async updateDecision(
    id: string,
    updates: Partial<Decision>,
    userId?: string
  ): Promise<void> {
    const docRef = doc(db, DECISIONS_COLLECTION, id);

    const updateData: any = {
      ...updates,
      updatedAt: Timestamp.now(),
    };

    if (userId) {
      updateData.updatedBy = userId;
    }

    await updateDoc(docRef, updateData);

    // Audit log
    await auditUpdate(
      AuditEntityType.teamDecision,
      id,
      updates.title || 'Decision'
    );
  }

  /**
   * Delete decision
   */
  static async deleteDecision(id: string): Promise<void> {
    const decision = await this.getDecisionById(id);
    if (!decision) {
      throw new Error('Decision not found');
    }

    const docRef = doc(db, DECISIONS_COLLECTION, id);
    await deleteDoc(docRef);

    // Audit log
    await auditDelete(
      AuditEntityType.teamDecision,
      id,
      decision.title
    );
  }

  /**
   * Approve a decision
   */
  static async approveDecision(
    id: string,
    approverId: string,
    approverName: string,
    comments?: string
  ): Promise<void> {
    const decision = await this.getDecisionById(id);
    if (!decision) {
      throw new Error('Decision not found');
    }

    // Update approval workflow
    const workflow = decision.approvalWorkflow || [];
    const currentStep = workflow.find(step => step.status === 'pending');

    if (currentStep) {
      currentStep.status = 'approved';
      currentStep.approverId = approverId;
      currentStep.approverName = approverName;
      currentStep.actionDate = new Date();
      currentStep.comments = comments;
    }

    // Check if all steps are approved
    const allApproved = workflow.every(step => step.status === 'approved');

    await this.updateDecision(id, {
      approvalWorkflow: workflow,
      status: allApproved ? DecisionStatus.approved : DecisionStatus.pendingApproval,
      approvedBy: allApproved ? approverId : undefined,
      approvedByName: allApproved ? approverName : undefined,
      approvedAt: allApproved ? new Date() : undefined,
    }, approverId);

    // Audit log
    await AuditLogService.writeLog({
      action: allApproved ? AuditLogAction.decisionApproved : AuditLogAction.approvalStepCompleted,
      severity: 'info',
      description: `Decision ${allApproved ? 'approved' : 'approval step completed'}: ${decision.title}`,
      userId: approverId,
      entityType: AuditEntityType.teamDecision,
      entityId: id,
      entityName: decision.title,
      success: true,
      details: {
        approverName,
        allApproved,
        comments,
      },
    });
  }

  /**
   * Reject a decision
   */
  static async rejectDecision(
    id: string,
    approverId: string,
    approverName: string,
    reason: string
  ): Promise<void> {
    const decision = await this.getDecisionById(id);
    if (!decision) {
      throw new Error('Decision not found');
    }

    // Update approval workflow
    const workflow = decision.approvalWorkflow || [];
    const currentStep = workflow.find(step => step.status === 'pending');

    if (currentStep) {
      currentStep.status = 'rejected';
      currentStep.approverId = approverId;
      currentStep.approverName = approverName;
      currentStep.actionDate = new Date();
      currentStep.comments = reason;
    }

    await this.updateDecision(id, {
      approvalWorkflow: workflow,
      status: DecisionStatus.rejected,
      rejectedBy: approverId,
      rejectedByName: approverName,
      rejectedAt: new Date(),
      rejectionReason: reason,
    }, approverId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.decisionRejected,
      severity: 'warning',
      description: `Decision rejected: ${decision.title}`,
      userId: approverId,
      entityType: AuditEntityType.teamDecision,
      entityId: id,
      entityName: decision.title,
      success: true,
      details: {
        approverName,
        reason,
      },
    });
  }

  /**
   * Mark decision as implemented
   */
  static async markAsImplemented(
    id: string,
    implementationNotes?: string,
    userId?: string
  ): Promise<void> {
    const decision = await this.getDecisionById(id);
    if (!decision) {
      throw new Error('Decision not found');
    }

    if (decision.status !== DecisionStatus.approved) {
      throw new Error('Decision must be approved before it can be marked as implemented');
    }

    await this.updateDecision(id, {
      status: DecisionStatus.implemented,
      implementationDate: new Date(),
      implementationNotes,
    }, userId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.decisionImplemented,
      severity: 'info',
      description: `Decision implemented: ${decision.title}`,
      userId,
      entityType: AuditEntityType.teamDecision,
      entityId: id,
      entityName: decision.title,
      success: true,
      details: {
        implementationNotes,
      },
    });
  }

  /**
   * Reverse a decision
   */
  static async reverseDecision(
    id: string,
    reason: string,
    userId?: string
  ): Promise<void> {
    const decision = await this.getDecisionById(id);
    if (!decision) {
      throw new Error('Decision not found');
    }

    await this.updateDecision(id, {
      status: DecisionStatus.reversed,
      rejectionReason: reason,
    }, userId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.decisionUpdated,
      severity: 'warning',
      description: `Decision reversed: ${decision.title}`,
      userId,
      entityType: AuditEntityType.teamDecision,
      entityId: id,
      entityName: decision.title,
      success: true,
      details: {
        reason,
      },
    });
  }

  /**
   * Get decision analytics
   */
  static async getDecisionAnalytics(
    dateRange?: { start: Date; end: Date }
  ): Promise<DecisionAnalytics> {
    let decisions: Decision[];

    if (dateRange) {
      const q = query(
        collection(db, DECISIONS_COLLECTION),
        where('createdAt', '>=', Timestamp.fromDate(dateRange.start)),
        where('createdAt', '<=', Timestamp.fromDate(dateRange.end))
      );
      const snapshot = await getDocs(q);
      decisions = snapshot.docs.map(doc => this.docToDecision(doc.id, doc.data()));
    } else {
      decisions = await this.getAllDecisions();
    }

    const byStatus: Record<DecisionStatus, number> = {
      [DecisionStatus.draft]: 0,
      [DecisionStatus.pendingApproval]: 0,
      [DecisionStatus.approved]: 0,
      [DecisionStatus.rejected]: 0,
      [DecisionStatus.implemented]: 0,
      [DecisionStatus.reversed]: 0,
    };

    const byImpact: Record<DecisionImpact, number> = {
      [DecisionImpact.low]: 0,
      [DecisionImpact.medium]: 0,
      [DecisionImpact.high]: 0,
      [DecisionImpact.critical]: 0,
    };

    let totalImplementationTime = 0;
    let implementedCount = 0;

    decisions.forEach(decision => {
      byStatus[decision.status]++;
      byImpact[decision.impact]++;

      if (decision.status === DecisionStatus.implemented && decision.approvedAt && decision.implementationDate) {
        const timeDiff = decision.implementationDate.getTime() - decision.approvedAt.getTime();
        totalImplementationTime += timeDiff / (1000 * 60 * 60 * 24); // Convert to days
        implementedCount++;
      }
    });

    return {
      totalDecisions: decisions.length,
      byStatus,
      byImpact,
      avgImplementationTime: implementedCount > 0 ? totalImplementationTime / implementedCount : 0,
      pendingApprovalCount: byStatus[DecisionStatus.pendingApproval],
      implementedCount: byStatus[DecisionStatus.implemented],
      reversedCount: byStatus[DecisionStatus.reversed],
    };
  }

  /**
   * Convert Firestore document to Decision model
   */
  private static docToDecision(id: string, data: any): Decision {
    return {
      id,
      meetingId: data.meetingId || '',
      meetingTitle: data.meetingTitle || '',
      sourceTimestampStart: data.sourceTimestampStart,
      sourceTimestampEnd: data.sourceTimestampEnd,
      sourceTextSnippet: data.sourceTextSnippet,
      title: data.title || '',
      description: data.description || '',
      rationale: data.rationale,
      alternatives: data.alternatives || [],
      category: data.category || '',
      impact: data.impact || DecisionImpact.medium,
      decisionMaker: data.decisionMaker || '',
      decisionMakerId: data.decisionMakerId,
      affectedParties: data.affectedParties || [],
      implementationDate: data.implementationDate?.toDate(),
      implementationNotes: data.implementationNotes,
      status: data.status || DecisionStatus.draft,
      approvalWorkflow: data.approvalWorkflow || [],
      containsPHI: data.containsPHI || false,
      phiDataTypes: data.phiDataTypes || [],
      phiNotes: data.phiNotes,
      aiConfidence: data.aiConfidence || 0,
      aiExtractionNotes: data.aiExtractionNotes,
      tags: data.tags || [],
      relatedDecisionIds: data.relatedDecisionIds || [],
      customFields: data.customFields,
      createdBy: data.createdBy || '',
      createdByName: data.createdByName || '',
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      approvedBy: data.approvedBy,
      approvedByName: data.approvedByName,
      approvedAt: data.approvedAt?.toDate(),
      rejectedBy: data.rejectedBy,
      rejectedByName: data.rejectedByName,
      rejectedAt: data.rejectedAt?.toDate(),
      rejectionReason: data.rejectionReason,
    };
  }
}
